import {
  TagInput,
  type TagInputTag,
} from "@/components/common/TagInput/TagInput"
import { toast } from "@/components/common/Toast"
import { isLimitError } from "@/utils/limitErrors"
import { Check, Plus } from "lucide-react"
import * as React from "react"

import { LoadingSpinner } from "@/components/common/LoadingSpinner/LoadingSpinner"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  CATEGORY_COLOR_OPTIONS,
  DEFAULT_CATEGORY_COLOR,
} from "@/constants/KnowledgeBaseConstant"
import {
  useCreateKnowledgeBaseCategory,
  useCreateKnowledgeBaseTag,
  useDeleteKnowledgeBaseCategory,
  useDeleteKnowledgeBaseTag,
  useUpdateKnowledgeBaseCategory,
  useUpdateKnowledgeBaseTag,
} from "@/hooks/useKnowledgeBaseCategories/useKnowledgeBaseCategories"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { brandColors } from "@/lib/brandColors"
import { cn } from "@/lib/utils"
import { KnowledgeBaseCategory } from "@/types/knowledgebaseCategories"
import { knowledgeBaseErrorKey } from "@/utils/knowledgeBaseErrors"
import { IconTrash } from "@tabler/icons-react"

// Backend limit for display_name_en / display_name_de (KbCategoryCreate, KbTagCreate).
const DISPLAY_NAME_MAX_LENGTH = 255

// Same rule as the backend's name_key (services/kb_slug.py).
const normalizeName = (value: string) =>
  value.normalize("NFKC").trim().replace(/\s+/g, " ").toLowerCase()

interface CreateKnowledgeBaseCategoryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  category?: KnowledgeBaseCategory | null
  onCategoryDeleted?: (categoryId: string) => void
}

export function CreateKnowledgeBaseCategoryDialog({
  open,
  onOpenChange,
  category = null,
  onCategoryDeleted,
}: CreateKnowledgeBaseCategoryDialogProps) {
  const { translate } = useTranslation()
  const [categoryName, setCategoryName] = React.useState("")
  const [selectedColor, setSelectedColor] = React.useState<string>(
    DEFAULT_CATEGORY_COLOR,
  )
  const [tags, setTags] = React.useState<TagInputTag[]>([])
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false)
  const { mutateAsync: createCategory, isPending: isCreatingCategory } =
    useCreateKnowledgeBaseCategory()
  const { mutateAsync: updateCategory, isPending: isUpdatingCategory } =
    useUpdateKnowledgeBaseCategory()
  const { mutateAsync: deleteCategory, isPending: isDeletingCategory } =
    useDeleteKnowledgeBaseCategory()
  const { mutateAsync: createTag, isPending: isCreatingTag } =
    useCreateKnowledgeBaseTag()
  const { mutateAsync: updateTag, isPending: isUpdatingTag } =
    useUpdateKnowledgeBaseTag()
  const { mutateAsync: deleteTag, isPending: isDeletingTag } =
    useDeleteKnowledgeBaseTag()

  const isEditMode = !!category
  const activeCategoryTags = React.useMemo(
    () =>
      [...(category?.tags ?? [])]
        .filter((tag) => tag.isActive)
        .sort((left, right) => left.sortOrder - right.sortOrder),
    [category],
  )
  const initialTags = React.useMemo<TagInputTag[]>(
    () =>
      activeCategoryTags.map((tag) => ({
        id: tag.id,
        text: tag.displayNameEn,
      })),
    [activeCategoryTags],
  )

  const isSubmitting =
    isCreatingCategory ||
    isUpdatingCategory ||
    isDeletingCategory ||
    isCreatingTag ||
    isUpdatingTag ||
    isDeletingTag

  const resetForm = React.useCallback(() => {
    setCategoryName("")
    setSelectedColor(DEFAULT_CATEGORY_COLOR)
    setTags([])
    setIsDeleteModalOpen(false)
  }, [])

  React.useEffect(() => {
    if (!open) {
      return
    }

    if (isEditMode && category) {
      const categoryColor = category.color || DEFAULT_CATEGORY_COLOR
      setCategoryName(category.displayNameEn)
      setSelectedColor(categoryColor)
      setTags(initialTags)
      return
    }

    resetForm()
  }, [category, initialTags, isEditMode, open, resetForm])

  const hasChanges = React.useMemo(() => {
    if (!isEditMode || !category) {
      return true
    }

    if (categoryName.trim() !== category.displayNameEn.trim()) {
      return true
    }

    if (selectedColor !== (category.color ?? DEFAULT_CATEGORY_COLOR)) {
      return true
    }

    if (tags.length !== initialTags.length) {
      return true
    }

    return tags.some((tag, index) => {
      const initialTag = initialTags[index]

      return (
        !initialTag || tag.id !== initialTag.id || tag.text !== initialTag.text
      )
    })
  }, [category, categoryName, initialTags, isEditMode, selectedColor, tags])

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && isSubmitting) {
        return
      }

      if (!nextOpen && !isSubmitting) {
        resetForm()
      }

      onOpenChange(nextOpen)
    },
    [isSubmitting, onOpenChange, resetForm],
  )

  const handleSubmit = React.useCallback(async () => {
    const trimmedCategoryName = categoryName.trim()
    // The backend treats names that differ only in case or spacing as the same
    // name, so a newly typed tag that repeats one is not sent. Existing tags are
    // always kept: leaving one out would delete it.
    const existingTagIds = new Set(activeCategoryTags.map((tag) => tag.id))
    const seenTagNames = new Set<string>()
    const skippedTagNames: string[] = []
    const tagDefinitions = tags
      .map((tag, index) => ({
        id: tag.id,
        displayNameEn: tag.text.trim(),
        displayNameDe: tag.text.trim(),
        sortOrder: index,
      }))
      .filter((tag) => {
        const key = normalizeName(tag.displayNameEn)
        if (existingTagIds.has(tag.id)) {
          seenTagNames.add(key)
          return true
        }
        if (!key || seenTagNames.has(key)) {
          if (key) skippedTagNames.push(tag.displayNameEn)
          return false
        }
        seenTagNames.add(key)
        return true
      })

    if (!trimmedCategoryName) {
      toast.error(translate("KNOWLEDGEBASE/NAME_INVALID_ERROR"))
      return
    }

    const saveCategory = async (): Promise<KnowledgeBaseCategory | null> => {
      const data = {
        displayNameEn: trimmedCategoryName,
        displayNameDe: trimmedCategoryName,
        color: selectedColor,
      }
      try {
        if (!isEditMode || !category) {
          return await createCategory(data)
        }
        if (
          category.displayNameEn !== trimmedCategoryName ||
          category.displayNameDe !== trimmedCategoryName ||
          (category.color ?? DEFAULT_CATEGORY_COLOR) !== selectedColor
        ) {
          await updateCategory({ categoryId: category.id, data })
        }
        return category
      } catch (error) {
        // Nothing was saved: keep the dialog open so the name can be fixed.
        if (!isLimitError(error)) {
          toast.error(
            translate(
              knowledgeBaseErrorKey(
                error,
                isEditMode
                  ? "KNOWLEDGEBASE/CATEGORY_UPDATE_ERROR"
                  : "KNOWLEDGEBASE/CATEGORY_CREATE_ERROR",
              ),
              { name: trimmedCategoryName },
            ),
          )
        }
        return null
      }
    }

    const savedCategory = await saveCategory()
    if (!savedCategory) {
      return
    }

    // The category is saved from here on. Each tag change succeeds or fails on
    // its own, and the dialog closes either way: staying open in create mode
    // would only make a retry hit "already exists" on the category.
    const tagChanges: {
      kind: "create" | "update" | "delete"
      name: string
      run: () => Promise<unknown>
    }[] = []
    const existingTagsById = new Map(
      activeCategoryTags.map((existingTag) => [existingTag.id, existingTag]),
    )
    for (const tag of tagDefinitions) {
      const data = {
        displayNameEn: tag.displayNameEn,
        displayNameDe: tag.displayNameDe,
        sortOrder: tag.sortOrder,
      }
      const existingTag = existingTagsById.get(tag.id)
      if (!existingTag) {
        tagChanges.push({
          kind: "create",
          name: tag.displayNameEn,
          run: () => createTag({ categoryId: savedCategory.id, data }),
        })
      } else if (
        existingTag.displayNameEn !== tag.displayNameEn ||
        existingTag.displayNameDe !== tag.displayNameDe ||
        existingTag.sortOrder !== tag.sortOrder
      ) {
        tagChanges.push({
          kind: "update",
          name: tag.displayNameEn,
          run: () =>
            updateTag({
              categoryId: savedCategory.id,
              tagId: existingTag.id,
              data,
            }),
        })
      }
    }
    const keptTagIds = new Set(tagDefinitions.map((tag) => tag.id))
    for (const existingTag of activeCategoryTags) {
      if (!keptTagIds.has(existingTag.id)) {
        tagChanges.push({
          kind: "delete",
          name: existingTag.displayNameEn,
          run: () =>
            deleteTag({ categoryId: savedCategory.id, tagId: existingTag.id }),
        })
      }
    }

    // Deletes first: removing "Foo" and adding "foo" in one save must not have
    // the create checked against a "Foo" that is still active.
    const runAll = (changes: typeof tagChanges) =>
      Promise.allSettled(changes.map((change) => change.run()))
    const deletes = tagChanges.filter((change) => change.kind === "delete")
    const writes = tagChanges.filter((change) => change.kind !== "delete")
    const results = [...(await runAll(deletes)), ...(await runAll(writes))]
    const failedTagNames = [...deletes, ...writes]
      .filter((_, index) => results[index].status === "rejected")
      .map((change) => change.name)

    if (failedTagNames.length > 0) {
      // A limit or trial error already got the interceptor's upgrade toast
      const hitLimit = results.some(
        (result) => result.status === "rejected" && isLimitError(result.reason),
      )
      if (!hitLimit) {
        toast.error(
          translate(
            isEditMode
              ? "KNOWLEDGEBASE/CATEGORY_UPDATE_TAGS_PARTIAL_ERROR"
              : "KNOWLEDGEBASE/CATEGORY_CREATE_TAGS_PARTIAL_ERROR",
            {
              category: trimmedCategoryName,
              tags: failedTagNames.join(", "),
            },
          ),
        )
      }
    } else {
      toast.success(
        translate(
          isEditMode
            ? "KNOWLEDGEBASE/CATEGORY_UPDATE_SUCCESS"
            : "KNOWLEDGEBASE/CATEGORY_CREATE_SUCCESS",
          { category: trimmedCategoryName },
        ),
      )
    }

    if (skippedTagNames.length > 0) {
      toast.warning(
        translate("KNOWLEDGEBASE/TAG_DUPLICATES_SKIPPED", {
          tags: skippedTagNames.join(", "),
        }),
      )
    }

    resetForm()
    onOpenChange(false)
  }, [
    activeCategoryTags,
    category,
    categoryName,
    createCategory,
    createTag,
    deleteTag,
    isEditMode,
    onOpenChange,
    resetForm,
    selectedColor,
    tags,
    translate,
    updateCategory,
    updateTag,
  ])

  const handleDeleteCategory = React.useCallback(async () => {
    if (!category) {
      return
    }

    try {
      await deleteCategory(category.id)
      toast.success(
        translate("KNOWLEDGEBASE/CATEGORY_DELETE_SUCCESS", {
          category: category.displayNameEn,
        }),
      )
      onCategoryDeleted?.(category.id)
      resetForm()
      onOpenChange(false)
    } catch {
      toast.error(translate("KNOWLEDGEBASE/CATEGORY_DELETE_ERROR"))
    } finally {
      setIsDeleteModalOpen(false)
    }
  }, [
    category,
    deleteCategory,
    onCategoryDeleted,
    onOpenChange,
    resetForm,
    translate,
  ])

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="z-70 sm:max-w-[520px]"
          overlayClassName="z-70"
        >
          <DialogHeader>
            <DialogTitle>
              {translate(
                isEditMode
                  ? "KNOWLEDGEBASE/CATEGORY_EDIT_DIALOG_TITLE"
                  : "KNOWLEDGEBASE/CATEGORY_CREATE_DIALOG_TITLE",
              )}
            </DialogTitle>
            <DialogDescription>
              {translate(
                isEditMode
                  ? "KNOWLEDGEBASE/CATEGORY_EDIT_DIALOG_DESCRIPTION"
                  : "KNOWLEDGEBASE/CATEGORY_CREATE_DIALOG_DESCRIPTION",
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="knowledgebase-category-name">
                {translate("KNOWLEDGEBASE/CATEGORY_CREATE_NAME_LABEL")}
              </Label>
              <Input
                id="knowledgebase-category-name"
                value={categoryName}
                onChange={(event) => setCategoryName(event.target.value)}
                maxLength={DISPLAY_NAME_MAX_LENGTH}
                placeholder={translate(
                  "KNOWLEDGEBASE/CATEGORY_CREATE_NAME_PLACEHOLDER",
                )}
                disabled={isSubmitting}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="knowledgebase-category-tags">
                {translate("KNOWLEDGEBASE/CATEGORY_CREATE_TAGS_LABEL")}
              </Label>
              <TagInput
                id="knowledgebase-category-tags"
                tags={tags}
                onTagsChange={setTags}
                placeholder={translate(
                  "KNOWLEDGEBASE/CATEGORY_CREATE_TAGS_PLACEHOLDER",
                )}
                disabled={isSubmitting}
                maxLength={DISPLAY_NAME_MAX_LENGTH}
                validate={(value) => normalizeName(value).length > 0}
              />
            </div>

            <div className="grid gap-2">
              <Label>
                {translate("KNOWLEDGEBASE/CATEGORY_CREATE_COLOR_LABEL")}
              </Label>
              <div
                className="flex flex-wrap gap-2"
                role="radiogroup"
                aria-label={translate(
                  "KNOWLEDGEBASE/CATEGORY_CREATE_COLOR_LABEL",
                )}
              >
                {CATEGORY_COLOR_OPTIONS.map((option) => {
                  const isSelected = option.color === selectedColor

                  return (
                    <button
                      key={option.name}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      aria-label={translate(
                        "KNOWLEDGEBASE/CATEGORY_CREATE_COLOR_ARIA",
                        { index: CATEGORY_COLOR_OPTIONS.indexOf(option) + 1 },
                      )}
                      onClick={() => setSelectedColor(option.color)}
                      disabled={isSubmitting}
                      className={cn(
                        "h-8 w-8 rounded-md border transition-all focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50",
                        isSelected
                          ? "border-2 border-white"
                          : "border-grey4 hover:border-grey5",
                      )}
                      style={{ backgroundColor: option.color }}
                    />
                  )
                })}
              </div>
            </div>
          </div>

          <DialogFooter className="sm:justify-between">
            {isEditMode ? (
              <Button
                variant="ghost"
                className="justify-start px-0 text-destructive hover:bg-transparent hover:text-destructive"
                onClick={() => setIsDeleteModalOpen(true)}
                disabled={isSubmitting}
              >
                <IconTrash className="size-4" />
                {translate("KNOWLEDGEBASE/CATEGORY_DELETE_ACTION")}
              </Button>
            ) : (
              <div />
            )}

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isSubmitting}
              >
                {translate("KNOWLEDGEBASE/CATEGORY_CREATE_CANCEL")}
              </Button>
              <Button
                onClick={() => void handleSubmit()}
                disabled={
                  isSubmitting ||
                  categoryName.trim().length === 0 ||
                  (isEditMode && !hasChanges)
                }
              >
                {isSubmitting ? (
                  <LoadingSpinner
                    size={16}
                    className="mr-2"
                    color={brandColors.grey2}
                  />
                ) : isEditMode ? (
                  <Check className="size-4" />
                ) : (
                  <Plus className="size-4" />
                )}
                {translate(
                  isEditMode
                    ? "KNOWLEDGEBASE/CATEGORY_EDIT_SUBMIT"
                    : "KNOWLEDGEBASE/CATEGORY_CREATE_SUBMIT",
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isEditMode && category && (
        <Dialog
          open={isDeleteModalOpen}
          onOpenChange={(nextOpen) => {
            if (!isSubmitting) {
              setIsDeleteModalOpen(nextOpen)
            }
          }}
        >
          <DialogPortal>
            <DialogOverlay className="z-70 bg-black/60" />
            <DialogContent className="z-71 max-w-[420px] border-grey4 bg-grey2 p-6 text-white">
              <DialogHeader className="items-center text-center sm:text-center">
                <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-red1/10 text-red1">
                  <IconTrash size={28} strokeWidth={1.75} />
                </div>
                <DialogTitle>
                  {translate("KNOWLEDGEBASE/CATEGORY_DELETE_ACTION")}
                </DialogTitle>
                <DialogDescription className="text-grey5">
                  {translate(
                    "KNOWLEDGEBASE/CATEGORY_DELETE_MODAL_DESCRIPTION",
                    {
                      category: category.displayNameEn,
                    },
                  )}
                </DialogDescription>
              </DialogHeader>

              <div className="flex gap-2.5 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setIsDeleteModalOpen(false)}
                  disabled={isSubmitting}
                >
                  {translate("KNOWLEDGEBASE/FILE_MODAL_SECONDARY_ACTION")}
                </Button>
                <Button
                  type="button"
                  className="flex-1"
                  onClick={() => void handleDeleteCategory()}
                  disabled={isSubmitting}
                >
                  {translate("KNOWLEDGEBASE/CATEGORY_DELETE_ACTION")}
                </Button>
              </div>
            </DialogContent>
          </DialogPortal>
        </Dialog>
      )}
    </>
  )
}
